import { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, useMap, Marker, Popup, Circle, GeoJSON } from "react-leaflet";
import { 
  Cloud, Droplets, Wind, Thermometer, Snowflake, Play, Pause, 
  Zap, Columns, History, SkipBack, SkipForward, Clock, TrendingUp,
  AlertTriangle, Calendar, ChevronLeft, ChevronRight, MapPin
} from "lucide-react";
import L from "leaflet";
import axios from "axios";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom marker icon
const locationIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `
    <div style="
      width: 24px;
      height: 24px;
      background: linear-gradient(135deg, #00f2ea, #7000ff);
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 10px rgba(0,0,0,0.3), 0 0 20px rgba(0,242,234,0.4);
      position: relative;
    ">
      <div style="
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 8px solid white;
      "></div>
    </div>
  `,
  iconSize: [24, 32],
  iconAnchor: [12, 32],
  popupAnchor: [0, -32]
});

// Map updater component
const MapUpdater = ({ lat, lon }) => {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lon], map.getZoom());
  }, [lat, lon, map]);
  return null;
};

const MapSizeInvalidator = () => {
  const map = useMap();

  useEffect(() => {
    const timers = [100, 500, 1200].map((delay) => setTimeout(() => map.invalidateSize(), delay));
    return () => timers.forEach(clearTimeout);
  }, [map]);

  return null;
};

const OPENWEATHER_API_KEY = process.env.REACT_APP_OWM_KEY;

// Weather layers config
const WEATHER_LAYERS = [
  { id: "precipitation", icon: Droplets, color: "#4ade80", label: "Rain" },
  { id: "snow", icon: Snowflake, color: "#93c5fd", label: "Snow" },
  { id: "clouds", icon: Cloud, color: "#d4d4d8", label: "Clouds" },
  { id: "wind", icon: Wind, color: "#ccff00", label: "Wind" },
  { id: "temp", icon: Thermometer, color: "#ff0080", label: "Temp" },
];

const PRECIP_INTENSITY = [
  { label: "Light", color: "#4ade80" },
  { label: "Moderate", color: "#facc15" },
  { label: "Heavy", color: "#f97316" },
  { label: "Severe", color: "#ef4444" },
  { label: "Extreme", color: "#ff00ff" },
];

// Outlook risk levels with SPC standard colors
const OUTLOOK_STYLES = {
  "TSTM": { color: "#88cc88", fillColor: "#88cc8840", weight: 2, dashArray: "5, 5" },
  "MRGL": { color: "#66cc66", fillColor: "#66cc6650", weight: 2 },
  "SLGT": { color: "#ffee00", fillColor: "#ffee0050", weight: 2 },
  "ENH": { color: "#ff9900", fillColor: "#ff990060", weight: 3 },
  "MDT": { color: "#ff0000", fillColor: "#ff000060", weight: 3 },
  "HIGH": { color: "#ff00ff", fillColor: "#ff00ff60", weight: 4 },
};

// Real SPC Outlook Layer
const SPCOutlookLayer = ({ enabled, outlookDay }) => {
  const [outlookData, setOutlookData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (enabled) {
      setLoading(true);
      // Fetch via backend proxy to avoid browser CORS failures from SPC
      fetch(`${API}/weather/outlook/geojson?day=${outlookDay}`)
        .then(res => res.json())
        .then(data => {
          setOutlookData(data);
          setLoading(false);
        })
        .catch(err => {
          setLoading(false);
        });
    } else {
      setOutlookData(null);
    }
  }, [enabled, outlookDay]);
  
  if (!enabled || !outlookData) return null;
  
  const getStyle = (feature) => {
    const label = feature.properties?.LABEL || feature.properties?.LABEL2 || "";
    let type = "TSTM";
    if (label.includes("MRGL")) type = "MRGL";
    else if (label.includes("SLGT")) type = "SLGT";
    else if (label.includes("ENH")) type = "ENH";
    else if (label.includes("MDT")) type = "MDT";
    else if (label.includes("HIGH")) type = "HIGH";
    
    const style = OUTLOOK_STYLES[type] || OUTLOOK_STYLES["TSTM"];
    return {
      color: style.color,
      fillColor: style.fillColor,
      fillOpacity: 0.4,
      weight: style.weight,
      dashArray: style.dashArray
    };
  };
  
  return (
    <GeoJSON 
      key={`outlook-${outlookDay}`}
      data={outlookData} 
      style={getStyle}
      onEachFeature={(feature, layer) => {
        const label = feature.properties?.LABEL || feature.properties?.LABEL2 || "Unknown";
        layer.bindPopup(`<strong>SPC Outlook</strong><br/>Category: ${label}`);
      }}
    />
  );
};

// RainViewer Real Radar Layer — actual weather radar imagery with animation
const RainViewerLayer = ({ enabled, playing, onFrameChange, isDarkMode }) => {
  const map = useMap();
  const layersRef = useRef([]);
  const framesRef = useRef([]);
  const frameIdxRef = useRef(0);
  const intervalRef = useRef(null);
  const onFrameChangeRef = useRef(onFrameChange);

  useEffect(() => {
    onFrameChangeRef.current = onFrameChange;
  }, [onFrameChange]);

  useEffect(() => {
    if (!enabled) {
      layersRef.current.forEach(l => map.removeLayer(l));
      layersRef.current = [];
      framesRef.current = [];
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    // Fetch RainViewer radar frames
    fetch("https://api.rainviewer.com/public/weather-maps.json")
      .then(res => res.json())
      .then(data => {
        const host = data.host;
        const past = data.radar?.past || [];
        if (!past.length) return;

        // Remove old layers
        layersRef.current.forEach(l => map.removeLayer(l));
        layersRef.current = [];

        // Create only the latest tile layer initially; update URL during playback.
        // This avoids preloading every radar frame at once, which caused many aborted tile requests.
        const tileSize = window.devicePixelRatio > 1 ? 512 : 256;
        const latestIdx = past.length - 1;
        const latest = past[latestIdx];
        const radarOpacity = isDarkMode ? 0.88 : 0.74;
        const layer = L.tileLayer(`${host}${latest.path}/${tileSize}/{z}/{x}/{y}/6/1_1.png`, {
          tileSize: 256,
          opacity: radarOpacity,
          maxNativeZoom: 7,
          maxZoom: 18,
          zIndex: 22,
        });
        layer.addTo(map);

        layersRef.current = [layer];
        framesRef.current = past;

        // Show the latest frame
        frameIdxRef.current = latestIdx;
        if (onFrameChangeRef.current) {
          const ts = latest.time;
          onFrameChangeRef.current(new Date(ts * 1000).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }), latestIdx, past.length, ts);
        }
      })
      .catch(err => console.error("RainViewer fetch error:", err.message));

    return () => {
      layersRef.current.forEach(l => map.removeLayer(l));
      layersRef.current = [];
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, map, isDarkMode]);

  // Animation control
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (playing && enabled && layersRef.current.length > 0 && framesRef.current.length > 1) {
      intervalRef.current = setInterval(() => {
        const layer = layersRef.current[0];
        const frames = framesRef.current;
        if (!layer || !frames.length) return;

        // Advance
        frameIdxRef.current = (frameIdxRef.current + 1) % frames.length;

        // Show new frame
        const tileSize = window.devicePixelRatio > 1 ? 512 : 256;
        const nextFrame = frames[frameIdxRef.current];
        layer.setUrl(`https://tilecache.rainviewer.com${nextFrame.path}/${tileSize}/{z}/{x}/{y}/6/1_1.png`);
        layer.setOpacity(isDarkMode ? 0.88 : 0.74);

        if (onFrameChangeRef.current && nextFrame) {
          const ts = nextFrame.time;
          onFrameChangeRef.current(new Date(ts * 1000).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }), frameIdxRef.current, frames.length, ts);
        }
      }, 1200);
    }

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [playing, enabled, isDarkMode]);

  return null;
};


// Enhanced Precipitation Visualization with realistic radar patterns
const RealisticPrecipLayer = ({ lat, lon, hoursAhead, dayOffset, enabled, precipType }) => {
  const [precipCells, setPrecipCells] = useState([]);
  const [animationFrame, setAnimationFrame] = useState(0);
  
  const totalHours = (dayOffset * 24) + hoursAhead;
  
  useEffect(() => {
    if (enabled) {
      // Generate realistic precipitation cells based on forecast
      axios.get(`${API}/weather/radar/future`, {
        params: { lat, lon, hours: Math.min(totalHours + 6, 72) }
      })
        .then(res => {
          if (res.data.success && res.data.frames) {
            const frameIndex = Math.min(Math.floor(totalHours / 3), res.data.frames.length - 1);
            const frame = res.data.frames[Math.max(0, frameIndex)];
            if (frame?.cells) {
              // Transform cells into realistic radar patterns
              const enhancedCells = generateRealisticCells(frame.cells, lat, lon, totalHours);
              setPrecipCells(enhancedCells);
            }
          }
        })
        .catch(() => {
          // Generate simulated realistic patterns
          const simulatedCells = generateSimulatedRadar(lat, lon, totalHours);
          setPrecipCells(simulatedCells);
        });
    } else {
      setPrecipCells([]);
    }
  }, [lat, lon, enabled, totalHours]);
  
  // Animation for moving precipitation
  useEffect(() => {
    if (enabled && precipCells.length > 0) {
      const interval = setInterval(() => {
        setAnimationFrame(prev => (prev + 1) % 10);
      }, 500);
      return () => clearInterval(interval);
    }
  }, [enabled, precipCells.length]);
  
  if (!enabled || precipCells.length === 0) return null;
  
  return (
    <>
      {precipCells.map((cell, idx) => (
        <PrecipitationCell 
          key={`precip-cell-${idx}`} 
          cell={cell} 
          animationFrame={animationFrame}
          precipType={precipType}
        />
      ))}
    </>
  );
};

// Individual precipitation cell with realistic shape
const PrecipitationCell = ({ cell, animationFrame, precipType }) => {
  const getRadarColors = (intensity, isSnow) => {
    if (isSnow) {
      // Snow colors - blues and purples
      if (intensity > 0.8) return { stroke: "#9333ea", fill: "#9333ea", opacity: 0.7 };
      if (intensity > 0.6) return { stroke: "#a855f7", fill: "#a855f7", opacity: 0.6 };
      if (intensity > 0.4) return { stroke: "#c4b5fd", fill: "#c4b5fd", opacity: 0.5 };
      if (intensity > 0.2) return { stroke: "#ddd6fe", fill: "#ddd6fe", opacity: 0.4 };
      return { stroke: "#ede9fe", fill: "#ede9fe", opacity: 0.3 };
    }
    // Rain colors - standard NEXRAD colors
    if (intensity > 0.9) return { stroke: "#ff00ff", fill: "#ff00ff", opacity: 0.8 }; // Purple - Extreme
    if (intensity > 0.75) return { stroke: "#cc0000", fill: "#cc0000", opacity: 0.75 }; // Dark Red - Heavy
    if (intensity > 0.6) return { stroke: "#ff0000", fill: "#ff0000", opacity: 0.7 }; // Red - Moderate-Heavy
    if (intensity > 0.45) return { stroke: "#ff6600", fill: "#ff6600", opacity: 0.65 }; // Orange
    if (intensity > 0.3) return { stroke: "#ffcc00", fill: "#ffcc00", opacity: 0.6 }; // Yellow
    if (intensity > 0.15) return { stroke: "#00cc00", fill: "#00cc00", opacity: 0.5 }; // Green
    return { stroke: "#00ff00", fill: "#00ff00", opacity: 0.4 }; // Light Green
  };
  
  const isSnow = cell.type === 'snow' || precipType === 'snow';
  const colors = getRadarColors(cell.intensity, isSnow);
  
  // Create organic blob shapes instead of circles
  const points = cell.shape || [];
  
  if (points.length === 0) {
    // Fallback to enhanced circle with gradient effect
    const baseRadius = 8000 + (cell.intensity * 40000);
    const pulseRadius = baseRadius + (animationFrame * 500 * cell.intensity);
    
    return (
      <>
        {/* Core precipitation area */}
        <Circle
          center={[cell.lat, cell.lon]}
          radius={baseRadius}
          pathOptions={{
            color: colors.stroke,
            fillColor: colors.fill,
            fillOpacity: colors.opacity,
            weight: 0
          }}
        >
          <Popup>
            <div className="text-gray-900">
              <strong>{isSnow ? '❄️ Snow' : '🌧️ Rain'}</strong><br/>
              Intensity: {(cell.intensity * 100).toFixed(0)}%<br/>
              {cell.amount > 0 && `Expected: ${(cell.amount / 25.4).toFixed(2)} in`}<br/>
              {cell.pop && `Chance: ${Math.round(cell.pop * 100)}%`}
            </div>
          </Popup>
        </Circle>
        
        {/* Outer gradient rings for radar effect */}
        {cell.intensity > 0.3 && (
          <Circle
            center={[cell.lat, cell.lon]}
            radius={baseRadius * 1.3}
            pathOptions={{
              color: 'transparent',
              fillColor: colors.fill,
              fillOpacity: colors.opacity * 0.4,
              weight: 0
            }}
          />
        )}
        {cell.intensity > 0.5 && (
          <Circle
            center={[cell.lat, cell.lon]}
            radius={baseRadius * 0.5}
            pathOptions={{
              color: 'transparent',
              fillColor: getRadarColors(Math.min(cell.intensity + 0.2, 1), isSnow).fill,
              fillOpacity: colors.opacity * 1.2,
              weight: 0
            }}
          />
        )}
      </>
    );
  }
  
  // Polygon for complex shapes
  return (
    <GeoJSON
      data={{
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [points]
        }
      }}
      style={{
        color: colors.stroke,
        fillColor: colors.fill,
        fillOpacity: colors.opacity,
        weight: 1
      }}
    >
      <Popup>
        <div className="text-gray-900">
          <strong>{isSnow ? '❄️ Snow' : '🌧️ Rain'}</strong><br/>
          Intensity: {(cell.intensity * 100).toFixed(0)}%
        </div>
      </Popup>
    </GeoJSON>
  );
};

// Generate realistic precipitation cells from API data
const generateRealisticCells = (apiCells, centerLat, centerLon, hours) => {
  const cells = [];
  
  apiCells.forEach((apiCell, idx) => {
    // Create main cell
    const intensity = apiCell.intensity === 'heavy' ? 0.8 : 
                     apiCell.intensity === 'moderate' ? 0.5 : 0.25;
    
    cells.push({
      lat: apiCell.lat,
      lon: apiCell.lon,
      intensity: intensity + (Math.random() * 0.2),
      type: apiCell.snow > 0 ? 'snow' : 'rain',
      amount: apiCell.rain || apiCell.snow || 0,
      pop: apiCell.pop || 0.5,
      shape: generateBlobShape(apiCell.lat, apiCell.lon, intensity)
    });
    
    // Add surrounding cells for larger systems
    if (intensity > 0.5) {
      for (let i = 0; i < 3; i++) {
        const angle = (i / 3) * Math.PI * 2 + Math.random() * 0.5;
        const dist = 0.15 + Math.random() * 0.1;
        cells.push({
          lat: apiCell.lat + Math.cos(angle) * dist,
          lon: apiCell.lon + Math.sin(angle) * dist,
          intensity: intensity * (0.5 + Math.random() * 0.3),
          type: apiCell.snow > 0 ? 'snow' : 'rain',
          amount: (apiCell.rain || apiCell.snow || 0) * 0.5,
          pop: (apiCell.pop || 0.5) * 0.8,
          shape: []
        });
      }
    }
  });
  
  return cells;
};

// Generate simulated radar patterns
const generateSimulatedRadar = (lat, lon, hours) => {
  const cells = [];
  const numCells = 5 + Math.floor(Math.random() * 10);
  const season = new Date().getMonth();
  const isWinter = season >= 11 || season <= 2;
  
  for (let i = 0; i < numCells; i++) {
    // Spread cells across the visible area
    const angle = Math.random() * Math.PI * 2;
    const distance = 0.3 + Math.random() * 1.5;
    const cellLat = lat + Math.cos(angle) * distance;
    const cellLon = lon + Math.sin(angle) * distance * 1.3;
    
    const intensity = Math.random();
    const isSnow = isWinter && Math.random() > 0.5;
    
    cells.push({
      lat: cellLat,
      lon: cellLon,
      intensity: intensity,
      type: isSnow ? 'snow' : 'rain',
      amount: intensity * (isSnow ? 5 : 10),
      pop: 0.4 + intensity * 0.5,
      shape: intensity > 0.4 ? generateBlobShape(cellLat, cellLon, intensity) : []
    });
    
    // Add clustered cells for storm systems
    if (intensity > 0.6) {
      const clusterSize = Math.floor(intensity * 5);
      for (let j = 0; j < clusterSize; j++) {
        const clusterAngle = Math.random() * Math.PI * 2;
        const clusterDist = 0.05 + Math.random() * 0.15;
        cells.push({
          lat: cellLat + Math.cos(clusterAngle) * clusterDist,
          lon: cellLon + Math.sin(clusterAngle) * clusterDist,
          intensity: intensity * (0.4 + Math.random() * 0.4),
          type: isSnow ? 'snow' : 'rain',
          amount: intensity * (isSnow ? 3 : 6),
          pop: 0.3 + intensity * 0.4,
          shape: []
        });
      }
    }
  }
  
  return cells;
};

// Generate organic blob shape for precipitation cells
const generateBlobShape = (centerLat, centerLon, intensity) => {
  const points = [];
  const numPoints = 12 + Math.floor(Math.random() * 8);
  const baseRadius = 0.1 + intensity * 0.15;
  
  for (let i = 0; i < numPoints; i++) {
    const angle = (i / numPoints) * Math.PI * 2;
    const variance = 0.7 + Math.random() * 0.6;
    const radius = baseRadius * variance;
    
    points.push([
      centerLon + Math.cos(angle) * radius * 1.3,
      centerLat + Math.sin(angle) * radius
    ]);
  }
  
  // Close the polygon
  points.push(points[0]);
  
  return points;
};

// Real Future Precipitation Layer - supports up to 3 days
const FuturePrecipLayer = ({ lat, lon, hoursAhead, dayOffset, enabled }) => {
  const [precipData, setPrecipData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Calculate total hours from now
  const totalHours = (dayOffset * 24) + hoursAhead;
  
  useEffect(() => {
    if (enabled && totalHours >= 0) {
      setLoading(true);
      // Fetch forecast data for the specified time range
      axios.get(`${API}/weather/radar/future`, {
        params: { lat, lon, hours: Math.min(totalHours + 6, 72) }
      })
        .then(res => {
          if (res.data.success) {
            setPrecipData(res.data.frames || []);
          }
          setLoading(false);
        })
        .catch(err => {
          setLoading(false);
        });
    }
  }, [lat, lon, enabled, totalHours]);
  
  if (!enabled || precipData.length === 0) return null;
  
  // Get the frame for the selected hour (closest available)
  const frameIndex = Math.min(Math.floor(totalHours / 3), precipData.length - 1);
  const currentFrame = precipData[Math.max(0, frameIndex)];
  if (!currentFrame || !currentFrame.cells) return null;
  
  const getColor = (intensity, pop) => {
    if (intensity === "heavy") return { color: "#ff0080", fill: "#ff008070" };
    if (intensity === "moderate") return { color: "#00f2ea", fill: "#00f2ea60" };
    if (pop > 0.5) return { color: "#00f2ea", fill: "#00f2ea50" };
    return { color: "#00f2ea", fill: "#00f2ea30" };
  };
  
  return (
    <>
      {currentFrame.cells.map((cell, idx) => {
        const colors = getColor(cell.intensity, cell.pop);
        const radius = cell.intensity === "heavy" ? 50000 : cell.intensity === "moderate" ? 40000 : 30000;
        
        return (
          <Circle
            key={`precip-${idx}`}
            center={[cell.lat, cell.lon]}
            radius={radius}
            pathOptions={{
              color: colors.color,
              fillColor: colors.fill,
              fillOpacity: Math.min(cell.pop, 0.7),
              weight: 1
            }}
          >
            <Popup>
              <strong>{cell.weather || "Precipitation"}</strong><br/>
              Chance: {Math.round(cell.pop * 100)}%<br/>
              {cell.rain > 0 && `Rain: ${(cell.rain / 25.4).toFixed(2)} in`}
              {cell.snow > 0 && `Snow: ${(cell.snow / 25.4).toFixed(2)} in`}
            </Popup>
          </Circle>
        );
      })}
    </>
  );
};

// Lightning layer (uses real alerts when available)
const LightningLayer = ({ lat, lon, enabled }) => {
  const [strikes, setStrikes] = useState([]);
  const [flashingStrikes, setFlashingStrikes] = useState(new Set());
  
  useEffect(() => {
    if (enabled) {
      // In production, this would fetch from a lightning data API
      // For now, we'll check if there are thunderstorms in the forecast
      axios.get(`${API}/weather/current`, { params: { lat, lon } })
        .then(res => {
          const weather = res.data?.weather?.[0]?.main?.toLowerCase() || "";
          if (weather.includes("thunder") || weather.includes("storm")) {
            // Generate lightning near the location
            const newStrikes = [];
            for (let i = 0; i < 10; i++) {
              newStrikes.push({
                id: i,
                lat: lat + (Math.random() - 0.5) * 1.5,
                lon: lon + (Math.random() - 0.5) * 1.5,
              });
            }
            setStrikes(newStrikes);
          } else {
            setStrikes([]);
          }
        })
        .catch(() => setStrikes([]));
    } else {
      setStrikes([]);
    }
  }, [lat, lon, enabled]);
  
  useEffect(() => {
    if (enabled && strikes.length > 0) {
      const flashInterval = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * strikes.length);
        setFlashingStrikes(prev => new Set([...prev, randomIndex]));
        setTimeout(() => {
          setFlashingStrikes(prev => {
            const next = new Set(prev);
            next.delete(randomIndex);
            return next;
          });
        }, 200);
      }, 600);
      return () => clearInterval(flashInterval);
    }
  }, [enabled, strikes.length]);
  
  if (!enabled || strikes.length === 0) return null;
  
  return (
    <>
      {strikes.map((strike, idx) => (
        <Circle
          key={strike.id}
          center={[strike.lat, strike.lon]}
          radius={flashingStrikes.has(idx) ? 15000 : 8000}
          pathOptions={{
            color: flashingStrikes.has(idx) ? '#ffffff' : '#ffff00',
            fillColor: flashingStrikes.has(idx) ? '#ffffff' : '#ffff00',
            fillOpacity: flashingStrikes.has(idx) ? 0.9 : 0.5,
            weight: flashingStrikes.has(idx) ? 3 : 1
          }}
        />
      ))}
    </>
  );
};

// Single map view component
const SingleMapView = ({ 
  lat, lon, locationName, activeLayers, showLightning, 
  mode, futureHours, dayOffset, showOutlook, outlookDay,
  showRealRadar, radarPlaying, onRadarFrame, isDarkMode
}) => {
  const baseMapUrl = isDarkMode
    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

  return (
    <MapContainer
      center={[lat, lon]}
      zoom={7}
      className="h-full w-full rounded-2xl"
      style={{ height: "100%", minHeight: "430px" }}
      zoomControl={false}
      scrollWheelZoom={true}
    >
      {/* Theme-aware high-contrast base map */}
      <TileLayer
        key={isDarkMode ? "radar-dark-basemap" : "radar-light-basemap"}
        url={baseMapUrl}
        attribution='&copy; OpenStreetMap &copy; CARTO'
        opacity={1}
        maxZoom={19}
      />
      
      {/* Location Marker */}
      <Marker position={[lat, lon]} icon={locationIcon}>
        <Popup>
          <div className="text-sm font-semibold text-gray-900">
            {locationName || "Your Location"}
          </div>
        </Popup>
      </Marker>
      
      {/* Current Weather Layers (Past/Present mode) */}
      {mode !== "future" && (
        <>
          {activeLayers.includes("precipitation") && (
            <TileLayer
              url={`https://tile.openweathermap.org/map/precipitation_new/{z}/{x}/{y}.png?appid=${OPENWEATHER_API_KEY}`}
              opacity={0.68}
              zIndex={14}
            />
          )}
          
          {activeLayers.includes("snow") && (
            <TileLayer
              url={`https://tile.openweathermap.org/map/snow/{z}/{x}/{y}.png?appid=${OPENWEATHER_API_KEY}`}
              opacity={0.92}
              zIndex={18}
            />
          )}
          
          {activeLayers.includes("clouds") && (
            <TileLayer
              url={`https://tile.openweathermap.org/map/clouds_new/{z}/{x}/{y}.png?appid=${OPENWEATHER_API_KEY}`}
              opacity={0.18}
              zIndex={8}
            />
          )}
          
          {activeLayers.includes("wind") && (
            <TileLayer
              url={`https://tile.openweathermap.org/map/wind_new/{z}/{x}/{y}.png?appid=${OPENWEATHER_API_KEY}`}
              opacity={0.5}
              zIndex={6}
            />
          )}
          
          {activeLayers.includes("temp") && (
            <TileLayer
              url={`https://tile.openweathermap.org/map/temp_new/{z}/{x}/{y}.png?appid=${OPENWEATHER_API_KEY}`}
              opacity={0.48}
              zIndex={7}
            />
          )}
        </>
      )}
      
      {/* Enhanced Realistic Future Precipitation */}
      <RealisticPrecipLayer 
        lat={lat} 
        lon={lon} 
        hoursAhead={futureHours}
        dayOffset={dayOffset || 0}
        enabled={mode === "future"} 
        precipType={activeLayers.includes("snow") ? "snow" : "rain"}
      />
      
      {/* Real SPC Outlook Zones */}
      <SPCOutlookLayer 
        enabled={showOutlook}
        outlookDay={outlookDay}
      />
      
      {/* Lightning (based on real weather) */}
      <LightningLayer lat={lat} lon={lon} enabled={showLightning} />

      {/* RainViewer Real Radar */}
      <RainViewerLayer enabled={showRealRadar} playing={radarPlaying} onFrameChange={onRadarFrame} isDarkMode={isDarkMode} />
      
      <MapUpdater lat={lat} lon={lon} />
      <MapSizeInvalidator />
    </MapContainer>
  );
};

export const RadarMap = ({ lat, lon, locationName, isDarkMode = true }) => {
  const [activeLayers, setActiveLayers] = useState(["precipitation", "snow"]);
  const [secondaryLayers, setSecondaryLayers] = useState(["wind"]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(50);
  const [totalFrames] = useState(100);
  const [showLightning, setShowLightning] = useState(false);
  const [showOutlook, setShowOutlook] = useState(false);
  const [showRealRadar, setShowRealRadar] = useState(true);
  const [radarPlaying, setRadarPlaying] = useState(false);
  const [radarTimeLabel, setRadarTimeLabel] = useState("");
  const [radarFrameIdx, setRadarFrameIdx] = useState(0);
  const [radarFrameCount, setRadarFrameCount] = useState(0);
  const [radarEpoch, setRadarEpoch] = useState(null);
  const [outlookDay, setOutlookDay] = useState(1);
  const [viewMode, setViewMode] = useState("single");
  const [timeMode, setTimeMode] = useState("past");
  const [futureHours, setFutureHours] = useState(0);
  const [selectedDay, setSelectedDay] = useState(0); // 0 = Today, 1 = Tomorrow, 2 = Day 3
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const intervalRef = useRef(null);

  // Get day labels
  const getDayLabel = (dayOffset) => {
    const date = new Date();
    date.setDate(date.getDate() + dayOffset);
    if (dayOffset === 0) return "Today";
    if (dayOffset === 1) return "Tomorrow";
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Calculate total hours based on selected day
  const getHoursForDay = () => {
    return selectedDay * 24;
  };

  // Calculate time label based on frame position
  const getTimeLabel = () => {
    if (timeMode === "future") {
      const totalHours = getHoursForDay() + futureHours;
      if (totalHours === 0) return "Now";
      
      const date = new Date();
      date.setHours(date.getHours() + totalHours);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    }
    
    const minutesFromNow = (currentFrame - 50) * 6;
    if (minutesFromNow === 0) return "Now";
    if (minutesFromNow < 0) {
      const absMinutes = Math.abs(minutesFromNow);
      if (absMinutes < 60) return `-${absMinutes}m`;
      const hours = Math.floor(absMinutes / 60);
      const mins = absMinutes % 60;
      return `-${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
    } else {
      if (minutesFromNow < 60) return `+${minutesFromNow}m`;
      const hours = Math.floor(minutesFromNow / 60);
      const mins = minutesFromNow % 60;
      return `+${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
    }
  };

  // Playback control for past mode
  useEffect(() => {
    if (isPlaying && timeMode === "past") {
      intervalRef.current = setInterval(() => {
        setCurrentFrame(prev => prev >= totalFrames - 1 ? 0 : prev + 1);
      }, 500 / playbackSpeed);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isPlaying, totalFrames, playbackSpeed, timeMode]);

  // Playback control for future mode - handles 3 days
  useEffect(() => {
    if (isPlaying && timeMode === "future") {
      intervalRef.current = setInterval(() => {
        setFutureHours(prev => {
          const maxHours = 23; // 0-23 hours per day
          if (prev >= maxHours) {
            // Move to next day if available
            if (selectedDay < 2) {
              setSelectedDay(d => d + 1);
              return 0;
            }
            // Reset to beginning
            setSelectedDay(0);
            return 0;
          }
          return prev + 1;
        });
      }, 800 / playbackSpeed);
    }
    return () => { if (intervalRef.current && timeMode === "future") clearInterval(intervalRef.current); };
  }, [isPlaying, playbackSpeed, timeMode, selectedDay]);

  const toggleLayer = (layerId, isSecondary = false) => {
    if (isSecondary) {
      setSecondaryLayers(prev => 
        prev.includes(layerId) ? prev.filter(id => id !== layerId) : [...prev, layerId]
      );
    } else {
      setActiveLayers(prev => 
        prev.includes(layerId) ? prev.filter(id => id !== layerId) : [...prev, layerId]
      );
    }
  };

  const skipFrames = (amount) => {
    if (timeMode === "past") {
      setCurrentFrame(prev => {
        const next = prev + amount;
        if (next < 0) return totalFrames + next;
        if (next >= totalFrames) return next - totalFrames;
        return next;
      });
    } else {
      setFutureHours(prev => Math.max(0, Math.min(23, prev + (amount > 0 ? 1 : -1))));
    }
  };

  const radarFreshnessMinutes = radarEpoch ? Math.max(0, Math.round((Date.now() / 1000 - radarEpoch) / 60)) : null;
  const radarFreshnessLabel = radarFreshnessMinutes === null
    ? "Waiting for RainViewer"
    : radarFreshnessMinutes <= 12
      ? `${radarFreshnessMinutes} min old`
      : `Updated ${radarFreshnessMinutes} min ago`;

  const shellClass = isDarkMode
    ? "high-contrast-radar radar-dark-contrast w-full h-full min-h-[720px] rounded-2xl border border-white/10 bg-[#0A0A0A]/80 p-4 shadow-[0_8px_32px_rgba(0,0,0,0.22)] backdrop-blur-xl"
    : "high-contrast-radar radar-light-contrast w-full h-full min-h-[720px] rounded-2xl border border-slate-200 bg-slate-50/95 p-4 shadow-[0_8px_32px_rgba(15,23,42,0.12)] backdrop-blur-xl";
  const textPrimary = isDarkMode ? "text-white" : "text-slate-950";
  const textSecondary = isDarkMode ? "text-white/55" : "text-slate-600";
  const subtleText = isDarkMode ? "text-white/40" : "text-slate-500";
  const panelClass = isDarkMode
    ? "rounded-xl border border-white/10 bg-white/[0.04] p-4"
    : "rounded-xl border border-slate-200 bg-white p-4 shadow-sm";
  const innerPanelClass = isDarkMode
    ? "rounded-lg border border-white/10 bg-black/30 p-3"
    : "rounded-lg border border-slate-200 bg-slate-100 p-3";
  const inactiveButtonClass = isDarkMode
    ? "border-white/10 bg-black/25 text-white/50 hover:text-white"
    : "border-slate-200 bg-white text-slate-500 hover:text-slate-950";
  const selectedLightButtonClass = isDarkMode
    ? "bg-white/10 text-white"
    : "bg-slate-200 text-slate-950";
  const timelinePanelClass = isDarkMode
    ? "mt-4 rounded-xl border border-white/10 bg-white/[0.04] p-4"
    : "mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm";
  const timelineMutedText = isDarkMode ? "text-white/50" : "text-slate-500";
  const softRoundButtonClass = isDarkMode
    ? "bg-black/30 text-white disabled:opacity-30"
    : "bg-slate-100 text-slate-700 disabled:opacity-30";
  const inactivePillClass = isDarkMode
    ? "bg-black/30 text-white/55 hover:text-white"
    : "bg-slate-100 text-slate-600 hover:text-slate-950";

  return (
    <div className={shellClass} data-testid="radar-map-container" data-radar-theme={isDarkMode ? "dark" : "light"}>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-emerald-300/20 bg-emerald-300/10">
            <TrendingUp className="h-5 w-5 text-emerald-300" />
          </div>
          <div>
            <h3 className={`text-lg font-black ${textPrimary}`}>High-Contrast Live Radar</h3>
            <p className={`text-sm ${textSecondary}`}>RainViewer NEXRAD colors over a {isDarkMode ? "dark" : "light"} map for cleaner rain, snow, and cloud separation.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={`rounded-full border px-3 py-1.5 font-semibold ${showRealRadar ? 'border-emerald-300/30 bg-emerald-300/10 text-emerald-200' : 'border-white/10 bg-white/5 text-white/55'}`} data-testid="radar-source-status">
            {showRealRadar ? 'Live radar on' : 'Live radar off'}
          </span>
          <span className={`rounded-full border px-3 py-1.5 font-mono ${isDarkMode ? "border-white/10 bg-white/5 text-white/60" : "border-slate-200 bg-white text-slate-600"}`} data-testid="radar-freshness-label">
            {radarFreshnessLabel}
          </span>
        </div>
      </div>

      <div className={`relative mb-4 h-[430px] overflow-hidden rounded-xl border shadow-inner sm:aspect-video sm:h-auto ${isDarkMode ? "border-white/10 bg-black" : "border-slate-200 bg-slate-100"}`} data-testid="radar-map-viewport">
        {viewMode === 'single' ? (
          <SingleMapView
            lat={lat}
            lon={lon}
            locationName={locationName}
            activeLayers={activeLayers}
            showLightning={showLightning}
            mode={timeMode}
            futureHours={futureHours}
            dayOffset={selectedDay}
            showOutlook={showOutlook}
            outlookDay={outlookDay}
            showRealRadar={showRealRadar}
            radarPlaying={radarPlaying}
            isDarkMode={isDarkMode}
            onRadarFrame={(label, idx, total, epoch) => { setRadarTimeLabel(label); setRadarFrameIdx(idx); setRadarFrameCount(total); setRadarEpoch(epoch); }}
          />
        ) : (
          <div className="grid h-full min-h-[430px] grid-cols-1 gap-2 lg:grid-cols-2">
            <div className={`relative min-h-[300px] overflow-hidden rounded-xl border ${isDarkMode ? "border-white/10" : "border-slate-200"}`}>
              <div className={`absolute left-3 top-3 z-[1000] rounded-lg border px-3 py-1 text-xs font-semibold backdrop-blur-xl ${isDarkMode ? "border-white/10 bg-black/80 text-white/80" : "border-slate-200 bg-white/85 text-slate-700"}`} data-testid="radar-current-map-label">
                Current
              </div>
              <SingleMapView
                lat={lat} lon={lon} locationName={locationName}
                activeLayers={activeLayers} showLightning={showLightning}
                mode="past" futureHours={0} dayOffset={0} showOutlook={false} outlookDay={1}
                showRealRadar={showRealRadar} radarPlaying={radarPlaying} isDarkMode={isDarkMode}
                onRadarFrame={(label, idx, total, epoch) => { setRadarTimeLabel(label); setRadarFrameIdx(idx); setRadarFrameCount(total); setRadarEpoch(epoch); }}
              />
            </div>
            <div className={`relative min-h-[300px] overflow-hidden rounded-xl border ${isDarkMode ? "border-white/10" : "border-slate-200"}`}>
              <div className={`absolute left-3 top-3 z-[1000] rounded-lg border px-3 py-1 text-xs font-semibold text-pink-300 backdrop-blur-xl ${isDarkMode ? "border-pink-300/20 bg-black/80" : "border-pink-200 bg-white/85"}`} data-testid="radar-future-map-label">
                {getDayLabel(selectedDay)} {futureHours > 0 ? `${futureHours}:00` : ''}
              </div>
              <SingleMapView
                lat={lat} lon={lon} locationName={locationName}
                activeLayers={secondaryLayers} showLightning={false}
                mode="future" futureHours={futureHours} dayOffset={selectedDay} showOutlook={showOutlook} outlookDay={outlookDay}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <section className={panelClass} data-testid="radar-layer-panel">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <p className={`text-xs font-bold uppercase tracking-[0.18em] ${subtleText}`}>Layers</p>
              <h4 className={`font-black ${textPrimary}`}>Contrast controls</h4>
            </div>
            <MapPin className="h-4 w-4 text-[#00f2ea]" />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-2">
            {WEATHER_LAYERS.map((layer) => {
              const Icon = layer.icon;
              const active = activeLayers.includes(layer.id);
              return (
                <button
                  key={layer.id}
                  type="button"
                  onClick={() => toggleLayer(layer.id)}
                  data-testid={`radar-layer-toggle-${layer.id}`}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-[background-color,border-color,transform] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#00f2ea] ${active ? (isDarkMode ? 'border-white/20 bg-white/10 text-white' : 'border-slate-300 bg-slate-100 text-slate-950') : (isDarkMode ? 'border-white/10 bg-black/25 text-white/45 hover:text-white' : 'border-slate-200 bg-white text-slate-500 hover:text-slate-950')}`}
                >
                  <Icon className="h-4 w-4" style={{ color: active ? layer.color : '#71717a' }} />
                  {layer.label}
                </button>
              );
            })}
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {PRECIP_INTENSITY.map((item) => (
              <div key={item.label} className={`rounded-lg px-2 py-2 text-center ${isDarkMode ? "bg-black/30" : "bg-slate-100"}`} data-testid={`radar-intensity-${item.label.toLowerCase()}`}>
                <div className="mx-auto mb-1 h-3 w-full rounded" style={{ backgroundColor: item.color }} />
                <span className={`text-[10px] font-semibold ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>{item.label}</span>
              </div>
            ))}
            <div className={`rounded-lg px-2 py-2 text-center ${isDarkMode ? "bg-black/30" : "bg-slate-100"}`} data-testid="radar-intensity-snow">
              <div className="mx-auto mb-1 h-3 w-full rounded bg-blue-300" />
              <span className={`text-[10px] font-semibold ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>Snow</span>
            </div>
          </div>
        </section>

        <section className={panelClass} data-testid="radar-live-panel">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-300/70">RainViewer</p>
              <h4 className={`font-black ${textPrimary}`}>Live radar feed</h4>
            </div>
            <button
              type="button"
              onClick={() => { setShowRealRadar(!showRealRadar); setRadarPlaying(false); }}
              data-testid="real-radar-toggle"
              className={`rounded-full border px-3 py-1.5 text-xs font-black transition-[background-color,border-color,transform] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#00f2ea] ${showRealRadar ? 'border-emerald-300/40 bg-emerald-300/15 text-emerald-700 dark:text-emerald-200' : inactiveButtonClass}`}
            >
              {showRealRadar ? 'On' : 'Off'}
            </button>
          </div>
          <div className={`flex items-center gap-3 ${innerPanelClass}`}>
            <button
              type="button"
              onClick={() => setRadarPlaying(!radarPlaying)}
              disabled={!showRealRadar}
              data-testid="radar-play-btn"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-300/25 bg-emerald-300/10 text-emerald-200 transition-[background-color,transform] hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {radarPlaying ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className={`font-mono ${isDarkMode ? "text-white/70" : "text-slate-700"}`} data-testid="radar-frame-time">{radarTimeLabel || 'Latest frame'}</span>
                <span className={subtleText}>{radarFrameCount ? `${radarFrameIdx + 1}/${radarFrameCount}` : '—'}</span>
              </div>
              <div className="mt-2 flex gap-[3px]" data-testid="radar-frame-progress">
                {Array.from({ length: Math.max(1, Math.min(radarFrameCount || 12, 12)) }).map((_, i) => {
                  const step = Math.max(1, Math.floor((radarFrameCount || 12) / 12));
                  return <div key={`rframe-${i}`} className={`h-2 flex-1 rounded-full ${i * step <= radarFrameIdx ? 'bg-emerald-300' : 'bg-white/10'}`} />;
                })}
              </div>
            </div>
          </div>
          <p className={`mt-3 text-xs leading-relaxed ${isDarkMode ? "text-white/45" : "text-slate-500"}`}>
            NEXRAD color scheme separates rain in green/yellow/red from snow in blue for better storm readability.
          </p>
        </section>

        <section className={panelClass} data-testid="radar-time-panel">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <p className={`text-xs font-bold uppercase tracking-[0.18em] ${subtleText}`}>Timing</p>
              <h4 className={`font-black ${textPrimary}`}>Playback & outlook</h4>
            </div>
            <div className={`rounded-full px-2 py-1 text-xs font-mono ${timeMode === 'past' ? 'bg-[#00f2ea]/15 text-[#00f2ea]' : 'bg-pink-400/15 text-pink-300'}`} data-testid="radar-time-mode-label">
              {timeMode === 'past' ? 'HISTORY' : `DAY ${selectedDay + 1}`}
            </div>
          </div>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => { setTimeMode('past'); setIsPlaying(false); }}
              data-testid="radar-mode-past-button"
              className={`rounded-lg border px-3 py-2 text-sm font-bold transition-[background-color,border-color] ${timeMode === 'past' ? 'border-[#00f2ea]/35 bg-[#00f2ea]/15 text-[#00f2ea]' : inactiveButtonClass}`}
            >
              <History className="mr-1 inline h-4 w-4" /> Past
            </button>
            <button
              type="button"
              onClick={() => { setTimeMode('future'); setIsPlaying(false); setFutureHours(0); setSelectedDay(0); }}
              data-testid="radar-mode-future-button"
              className={`rounded-lg border px-3 py-2 text-sm font-bold transition-[background-color,border-color] ${timeMode === 'future' ? 'border-pink-300/35 bg-pink-400/15 text-pink-300' : inactiveButtonClass}`}
            >
              <Calendar className="mr-1 inline h-4 w-4" /> 3-Day
            </button>
          </div>
          <div className={`flex items-center justify-between gap-2 ${innerPanelClass.replace("p-3", "p-2")}`}>
            <button type="button" onClick={() => skipFrames(-10)} data-testid="radar-skip-back-button" className={`flex h-9 w-9 items-center justify-center rounded-full transition-[background-color] ${selectedLightButtonClass} hover:bg-white/20`}><SkipBack className="h-4 w-4" /></button>
            <button type="button" onClick={() => setIsPlaying(!isPlaying)} data-testid="radar-timeline-play-button" className={`flex h-11 w-11 items-center justify-center rounded-full border ${timeMode === 'past' ? 'border-[#00f2ea]/30 bg-[#00f2ea]/15 text-[#00f2ea]' : 'border-pink-300/30 bg-pink-400/15 text-pink-300'}`}>
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
            </button>
            <button type="button" onClick={() => skipFrames(10)} data-testid="radar-skip-forward-button" className={`flex h-9 w-9 items-center justify-center rounded-full transition-[background-color] ${selectedLightButtonClass} hover:bg-white/20`}><SkipForward className="h-4 w-4" /></button>
            <div className="ml-auto flex gap-1">
              {[0.5, 1, 2].map(speed => (
                <button key={speed} type="button" onClick={() => setPlaybackSpeed(speed)} data-testid={`radar-speed-${String(speed).replace('.', '-')}-button`} className={`rounded-md px-2 py-1 text-xs font-bold ${playbackSpeed === speed ? selectedLightButtonClass : (isDarkMode ? 'bg-black/20 text-white/45' : 'bg-slate-100 text-slate-500')}`}>{speed}x</button>
              ))}
            </div>
          </div>
        </section>
      </div>

      <section className={timelinePanelClass} data-testid="radar-timeline-panel">
        {timeMode === 'past' ? (
          <>
            <div className={`mb-2 flex items-center justify-between text-xs ${timelineMutedText}`}>
              <span>-5h</span>
              <span className="font-mono font-semibold text-[#00f2ea]" data-testid="radar-time-label">{getTimeLabel()}</span>
              <span>+5h</span>
            </div>
            <input
              type="range"
              min="0"
              max={totalFrames - 1}
              value={currentFrame}
              onChange={(e) => setCurrentFrame(parseInt(e.target.value))}
              data-testid="radar-history-slider"
              className={`h-2 w-full cursor-pointer appearance-none rounded-full accent-[#00f2ea] ${isDarkMode ? "bg-white/15" : "bg-slate-200"}`}
            />
          </>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { setSelectedDay(Math.max(0, selectedDay - 1)); setFutureHours(0); }} disabled={selectedDay === 0} data-testid="radar-day-prev-button" className={`rounded-full p-1.5 ${softRoundButtonClass}`}><ChevronLeft className="h-4 w-4" /></button>
                {[0, 1, 2].map(day => (
                  <button key={day} type="button" onClick={() => { setSelectedDay(day); setFutureHours(0); }} data-testid={`radar-day-${day + 1}-button`} className={`rounded-full px-3 py-1.5 text-sm font-bold ${selectedDay === day ? 'bg-pink-400 text-black' : inactivePillClass}`}>{getDayLabel(day)}</button>
                ))}
                <button type="button" onClick={() => { setSelectedDay(Math.min(2, selectedDay + 1)); setFutureHours(0); }} disabled={selectedDay === 2} data-testid="radar-day-next-button" className={`rounded-full p-1.5 ${softRoundButtonClass}`}><ChevronRight className="h-4 w-4" /></button>
              </div>
              <span className="font-mono text-sm font-bold text-pink-300" data-testid="radar-future-time-label">{getTimeLabel()}</span>
            </div>
            <input type="range" min="0" max="23" value={futureHours} onChange={(e) => setFutureHours(parseInt(e.target.value))} data-testid="radar-future-hour-slider" className={`h-2 w-full cursor-pointer appearance-none rounded-full accent-pink-400 ${isDarkMode ? "bg-white/15" : "bg-slate-200"}`} />
            <div className="mt-3 flex flex-wrap gap-2">
              {[0, 6, 12, 18, 23].map(h => {
                const hourLabel = h === 0 ? '12a' : h === 12 ? '12p' : h < 12 ? `${h}a` : `${h - 12}p`;
                return <button key={h} type="button" onClick={() => setFutureHours(h)} data-testid={`radar-hour-${h}-button`} className={`rounded-md px-2 py-1 text-xs font-bold ${futureHours === h ? 'bg-pink-400/25 text-pink-300' : (isDarkMode ? 'bg-black/30 text-white/45 hover:text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-950')}`}>{hourLabel}</button>;
              })}
            </div>
          </>
        )}
        <div className={`mt-4 flex flex-wrap items-center gap-2 border-t pt-3 ${isDarkMode ? "border-white/10" : "border-slate-200"}`}>
          <button type="button" onClick={() => setShowLightning(!showLightning)} data-testid="radar-lightning-toggle" className={`rounded-lg border px-3 py-2 text-sm font-bold ${showLightning ? 'border-yellow-300/35 bg-yellow-300/15 text-yellow-300' : inactiveButtonClass}`}><Zap className="mr-1 inline h-4 w-4" /> Lightning</button>
          <button type="button" onClick={() => setShowOutlook(!showOutlook)} data-testid="radar-outlook-toggle" className={`rounded-lg border px-3 py-2 text-sm font-bold ${showOutlook ? 'border-orange-300/35 bg-orange-300/15 text-orange-300' : inactiveButtonClass}`}><AlertTriangle className="mr-1 inline h-4 w-4" /> SPC Outlook</button>
          <button type="button" onClick={() => setViewMode(viewMode === 'single' ? 'split' : 'single')} data-testid="radar-split-view-toggle" className={`rounded-lg border px-3 py-2 text-sm font-bold ${viewMode === 'split' ? 'border-[#00f2ea]/35 bg-[#00f2ea]/15 text-[#00f2ea]' : inactiveButtonClass}`}><Columns className="mr-1 inline h-4 w-4" /> Split View</button>
          {showOutlook && [1, 2, 3].map(day => (
            <button key={day} type="button" onClick={() => setOutlookDay(day)} data-testid={`radar-outlook-day-${day}-button`} className={`rounded-md px-2 py-1 text-xs font-bold ${outlookDay === day ? 'bg-orange-300/25 text-orange-300' : (isDarkMode ? 'bg-black/25 text-white/45' : 'bg-slate-100 text-slate-500')}`}>Day {day}</button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default RadarMap;
